
'use client'
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

interface DocumentUploadProps {
  label: string;
  description?: string;
  /**
   * Identifier used to infer backend documentType
   * Expected ids: 'license' | 'insurance' | 'utility' | 'companyReg'
   */
  id: string;
}

const DocumentUpload = ({ label, description, id }: DocumentUploadProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const isUtility = id === 'utility' || id === 'utilityBill';

  const mapIdToDocumentType = (rawId: string): 'license' | 'insurance' | 'utilityBill' | 'companyReg' => {
    switch (rawId) {
      case 'license':
        return 'license';
      case 'insurance':
        return 'insurance';
      case 'companyReg':
        return 'companyReg';
      case 'utility':
      case 'utilityBill':
        return 'utilityBill';
      default:
        return 'license';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = async () => {
    try {
      if (!files.length) {
        toast.error("Please select file(s) first");
        return;
      }
      setIsUploading(true);

      const documentType = mapIdToDocumentType(id);
      const formData = new FormData();
      formData.append('documentType', documentType);
      const filesToSend = isUtility ? files : [files[0]];
      filesToSend.forEach((f) => formData.append('documents', f));

      const response = await fetch('/api/driver/documents', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result?.error) {
        throw new Error(result?.error || 'Upload failed');
      }

      toast.success(`${label} uploaded successfully`);
      setFiles([]);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload document(s)');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        {description && (
          <p className="text-sm text-gray-500 dark:text-muted-foreground">{description}</p>
        )}
        <div className="flex items-center gap-2 mt-2">
          <Input
            id={id}
            type="file"
            accept="image/*,application/pdf"
            multiple={isUtility}
            className="flex-1"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
        {files.length > 0 && (
          <p className="text-sm text-gray-500 dark:text-muted-foreground">
            Selected: {files.map(f => f.name).join(', ')}
          </p>
        )}
      </div>
    </Card>
  );
};

export default DocumentUpload;
